// LocalComputerDiscoveryPanel — Group D (2026-06-02 discovery redesign).
//
// Replaces the legacy `<DiscoveryPanel>` slot inside `<LocalComputerPanel>`.
//
// Design rationale (see docs/superpowers/specs/2026-06-02-discovery-redesign.md
// §"Group D"): Alexey explicitly preferred the Router-card peer-candidates UX
// over the LC's old wizard-style autodiscovery, and asked us to reuse the same
// component. We keep the master `subnet-scan` DiscoveryRun as the *underlying
// primitive* — that's how a jetson actually enumerates its LAN — but we drop
// the credential-prompt + auto-import pipeline and instead surface the raw
// `device_found` events as a peer list. The operator drives every per-host
// import explicitly through the same `<DiscoveredPeerList>` Group B built for
// the router card.
//
// What lives elsewhere (intentionally NOT touched by this commit):
//   - The wizard at `SetupWizardPage.tsx` still uses the old `<DiscoveryPanel>`
//     (timeline-style master flow). That surface has a different audience
//     (first-time setup with creds prompts), so leave it alone.
//   - The LC detail page's own `<DiscoveryPanel>` (curl + token panel) is a
//     completely different component despite the name clash; not in scope.

import { useCallback, useMemo, useState } from 'react';
import useSWR, { mutate as swrMutate } from 'swr';
import {
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  Wifi,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import {
  cancelDiscoveryRun,
  createDiscoveryRun,
  isLive,
  isTerminal,
  type DiscoveryRunStatus,
} from '@/api/discoveryRuns';
import { useDiscoveryRun } from '@/hooks/useDiscoveryRun';
import type { DiscoveryEvent } from '@/api/discovery';
import {
  checkRouterConnectivity,
  designateRouterHost,
  type RouterCheckKind,
  type RouterCheckResponse,
  type RouterHostDesignation,
  type RouterProbeHostKind,
} from '@/api/zoneRouter';
import { listProviders } from '@/api/providers';
import { listZoneNVRs } from '@/api/nvrs';
import {
  DiscoveredPeerBulkFooter,
  DiscoveredPeerList,
  effectiveKind,
  type DiscoveredPeer,
} from './DiscoveredPeerList';
import { SectionLabel } from './RouterPanel';
import { UnifiedCameraDialog } from './UnifiedCameraDialog';
import NewNVRCard from './NewNVRCard';
import NewRouterCard from './NewRouterCard';

const HASH_KEY = 'lc-discovery';

interface Props {
  /** LocalComputer UUID — passed to createDiscoveryRun. */
  lcId: string;
  /** Zone ID — passed to createDiscoveryRun + reused for designate/check
   *  endpoints (those are zone-scoped, not router-scoped). */
  zoneId: number | string;
  /** Fires after a successful import so the equipment tab refreshes the
   *  cameras/NVRs/router rows below. */
  onImported?: () => void;
}

/**
 * Top-level slot inside `LocalComputerPanel`. Two visible states:
 *
 *   1. Idle (no run, or run completed and dismissed) — small CTA
 *      "Сканировать сеть джетсона" anchored to the LC card.
 *   2. Scanning + results — header pill with status + cancel, then the
 *      peer list (same component as the Router card uses for its
 *      "Возможные камеры" / "Остальные устройства" sections).
 */
export function LocalComputerDiscoveryPanel({ lcId, zoneId, onImported }: Props) {
  const { t } = useT();

  // runId in URL hash so a refresh reattaches to an in-progress scan.
  // Mirrors `DiscoveryPanel`'s pattern but with a different key so the
  // two panels don't fight if both are on screen (they shouldn't be — LC
  // panel replaces the old one — but the hash key keeps that contract
  // explicit).
  const [runId, setRunId] = useState<string | null>(() => readHash());
  const [starting, setStarting] = useState(false);

  // ---- polling --------------------------------------------------------
  // `cancel` from the hook isn't destructured — we call the REST helper
  // directly in onCancel so the busy state is the SWR re-fetch flag,
  // not a separate spinner inside the hook.
  const { run, events, status, isLoading, refresh } = useDiscoveryRun(
    runId,
    { paused: false },
  );

  // ---- already-imported annotations (BE-side parity gap) -------------
  //
  // The probe→`device_found` events do NOT carry `imported_as_camera_id`
  // / `imported_via_nvr_id` annotations like Group A's router-probe does.
  // Surfacing "already added" status on LC peers therefore requires a
  // client-side join: look up the zone's existing providers + NVRs by IP
  // and mark matching peers. This is a known gap — when the BE eventually
  // ships the same annotations on `device_found` events, we drop this
  // join and use the wire-shape data directly.
  const { data: providers } = useSWR(
    zoneId ? `zone-providers-for-lc-discovery:${zoneId}` : null,
    () => listProviders(String(zoneId)).then(r => r.data.results),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const { data: nvrs } = useSWR(
    zoneId ? `zone-nvrs-for-lc-discovery:${zoneId}` : null,
    () => listZoneNVRs(zoneId).then(r => r.data.results),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  // ---- start / restart -----------------------------------------------
  const startScan = useCallback(async () => {
    if (starting) return;
    setStarting(true);
    try {
      const r = await createDiscoveryRun({
        local_computer: lcId,
        zone: typeof zoneId === 'string' ? parseInt(zoneId, 10) : zoneId,
        kind: 'master',
        target_spec: { kind: 'subnet-scan' },
      });
      writeHash(r.data.id);
      setRunId(r.data.id);
    } catch (err) {
      // Surface failures inline — the master endpoint returns 4xx with
      // detail, which we render in the status pill area. Stash on state.
      console.error('[lc-discovery] start failed', err);
    } finally {
      setStarting(false);
    }
  }, [starting, lcId, zoneId]);

  const onCancel = useCallback(async () => {
    if (!runId) return;
    try { await cancelDiscoveryRun(runId); } catch { /* swallow */ }
    void refresh();
  }, [runId, refresh]);

  const onRunAgain = useCallback(async () => {
    clearHash();
    setRunId(null);
    await startScan();
  }, [startScan]);

  const onDismiss = useCallback(() => {
    clearHash();
    setRunId(null);
  }, []);

  // ---- normalize events → peers --------------------------------------
  const peers = useMemo(
    () => buildPeersFromEvents(events, providers ?? [], nvrs ?? []),
    [events, providers, nvrs],
  );

  // Split peers by effective kind. LC peer list is one section rather
  // than two (router-card-style camera/NVR vs others split makes less
  // sense here — the LC sees everything on the LAN and the operator
  // wants to drive most decisions through the three-dots menu anyway).
  // Routers + cameras + NVRs go up top; "other" / "unknown" hosts go
  // into a collapsed section so the noise stays out of the way.
  const candidates = useMemo(
    () => peers.filter(p => {
      const k = effectiveKind(p);
      return k === 'camera' || k === 'nvr' || k === 'router';
    }),
    [peers],
  );
  const others = useMemo(
    () => peers.filter(p => {
      const k = effectiveKind(p);
      return k !== 'camera' && k !== 'nvr' && k !== 'router';
    }),
    [peers],
  );

  // ---- bulk-select state ---------------------------------------------
  const [bulkA, setBulkA] = useState(false);
  const [selectedA, setSelectedA] = useState<Set<string>>(() => new Set());
  const [bulkB, setBulkB] = useState(false);
  const [selectedB, setSelectedB] = useState<Set<string>>(() => new Set());

  const toggleSelect = (setter: typeof setSelectedA) => (mac: string) =>
    setter(prev => {
      const next = new Set(prev);
      if (prev.has(mac)) next.delete(mac);
      else next.add(mac);
      return next;
    });

  // ---- per-host import dialogs ---------------------------------------
  //
  // LC peers don't go through `importRouterCameras` (that endpoint is
  // about turning a router-side forward into a camera). Here, the LC IS
  // the destination — the jetson already sees the camera on the LAN at
  // its native :554. So we just need to plant a Camera + provider row
  // pointing at the peer's IP, which is exactly what the existing
  // `<UnifiedCameraDialog>` does. We pre-fill its inputs from the peer.
  const [cameraTarget, setCameraTarget] = useState<DiscoveredPeer | null>(null);
  const [nvrTarget, setNvrTarget] = useState<DiscoveredPeer | null>(null);
  const [routerTarget, setRouterTarget] = useState<DiscoveredPeer | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const startImport = useCallback((peer: DiscoveredPeer) => {
    setActionError(null);
    const kind = effectiveKind(peer);
    if (kind === 'nvr') setNvrTarget(peer);
    else if (kind === 'router') setRouterTarget(peer);
    else setCameraTarget(peer); // camera or unknown-with-import-button
  }, []);

  const doDesignate = useCallback(
    async (peer: DiscoveredPeer, designation: RouterHostDesignation) => {
      // The designate-host endpoint is keyed on (zone, mac), so it's
      // perfectly happy with hosts discovered through the LC as well —
      // no router required. Same primitive Group A shipped.
      const resp = await designateRouterHost(zoneId, {
        lan_host_ip: peer.ip,
        lan_host_mac: peer.mac,
        designation,
        suggested_name: peer.host_name || peer.ip || peer.mac,
      });
      if (!resp.ok && resp.error !== 'endpoint_not_deployed') {
        setActionError(resp.detail || resp.error);
      }
      // We don't have a re-probe primitive for LC peers (the run is a
      // streaming scan, not a cached snapshot), so the designation
      // override doesn't visually reflect on the current peer until the
      // next scan. That's an acceptable trade-off for v1 — running a
      // fresh scan after a designation is a single button press.
    },
    [zoneId],
  );

  const doCheck = useCallback(
    async (peer: DiscoveredPeer, kind: RouterCheckKind): Promise<RouterCheckResponse> => {
      // Known BE gap: `router/check/` requires the zone to have a
      // configured router with WG or public WAN — the endpoint uses the
      // router-side path to *annotate* the check source. For zones with
      // no router yet (the common LC-first scenario), this returns
      // `no_reachable_path` and the FE renders a friendly fail badge.
      // When BE adds an LC-source path for connectivity checks, this
      // call site stops failing on router-less zones.
      return checkRouterConnectivity(zoneId, {
        lan_host: peer.ip,
        kind,
        ...(kind === 'tcp_open' ? { port: 80 } : {}),
        ...(kind === 'rtsp_stream' ? { port: 554 } : {}),
      });
    },
    [zoneId],
  );

  // ---- render --------------------------------------------------------

  // No run yet — idle CTA card.
  if (!runId) {
    return (
      <section className="rounded-xl border border-dashed border-border bg-card/30 px-4 py-3.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            <SectionLabel>{t('equipment.lc.discovery.title')}</SectionLabel>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t('equipment.lc.discovery.idleHint')}
            </p>
          </div>
          <Button size="sm" onClick={() => { void startScan(); }} disabled={starting}>
            {starting ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Search className="size-3.5" />
            )}
            {t('equipment.lc.discovery.start')}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3.5 rounded-xl border border-border bg-card/30 px-4 py-3.5">
      <ScanHeader
        status={status}
        peersCount={peers.length}
        startedAt={run?.started_at}
        isLoading={isLoading}
        onCancel={() => { void onCancel(); }}
        onRunAgain={() => { void onRunAgain(); }}
        onDismiss={onDismiss}
      />

      {actionError && (
        <div
          className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="lc-discovery-error"
        >
          {actionError}
        </div>
      )}

      {peers.length === 0 && isLive(status) && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          {t('equipment.lc.discovery.searching')}
        </div>
      )}

      {peers.length === 0 && isTerminal(status) && (
        <div className="rounded-lg border border-dashed border-border px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
          {t('equipment.lc.discovery.emptyTerminal')}
        </div>
      )}

      {candidates.length > 0 && (
        <DiscoveredPeerList
          source="lc-peer"
          title={t('equipment.lc.discovery.candidatesTitle')}
          helpText={t('equipment.lc.discovery.candidatesHelp')}
          peers={candidates}
          selectionMode={bulkA}
          selectedMacs={selectedA}
          onToggleSelectionMode={() => {
            setBulkA(v => !v);
            if (bulkA) setSelectedA(new Set());
          }}
          onToggleSelect={toggleSelect(setSelectedA)}
          onImport={startImport}
          onDesignate={doDesignate}
          onCheck={doCheck}
        />
      )}

      {bulkA && selectedA.size > 0 && (
        <DiscoveredPeerBulkFooter
          selectedCount={selectedA.size}
          onCancel={() => { setBulkA(false); setSelectedA(new Set()); }}
          // No port-forward bulk action — LC peers don't go through the
          // forward path. Only "import as cameras" is offered; the
          // confirm just runs the per-host flow on the first selected
          // peer until the dedicated bulk-camera-create flow lands.
          onImportCameras={() => {
            const first = candidates.find(c => selectedA.has(c.mac));
            if (first) startImport(first);
          }}
        />
      )}

      {others.length > 0 && (
        <details className="group rounded-lg border border-border bg-card/40" data-testid="lc-discovery-section-others">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/80">
              {t('equipment.lc.discovery.othersTitle')}
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                {others.length}
              </span>
            </span>
            <ChevronDown className="size-4 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <div className="border-t border-border px-3 py-3">
            <DiscoveredPeerList
              source="lc-peer"
              title=""
              helpText={t('equipment.lc.discovery.othersHelp')}
              peers={others}
              selectionMode={bulkB}
              selectedMacs={selectedB}
              onToggleSelectionMode={() => {
                setBulkB(v => !v);
                if (bulkB) setSelectedB(new Set());
              }}
              onToggleSelect={toggleSelect(setSelectedB)}
              onImport={startImport}
              onDesignate={doDesignate}
              onCheck={doCheck}
            />
          </div>
        </details>
      )}

      {bulkB && selectedB.size > 0 && (
        <DiscoveredPeerBulkFooter
          selectedCount={selectedB.size}
          onCancel={() => { setBulkB(false); setSelectedB(new Set()); }}
          onImportCameras={() => {
            const first = others.find(c => selectedB.has(c.mac));
            if (first) startImport(first);
          }}
        />
      )}

      {/* Per-host import dialogs — each reuses the production primitive
          for its kind, pre-filled with the peer's identity. */}
      <UnifiedCameraDialog
        open={cameraTarget !== null}
        onOpenChange={v => { if (!v) setCameraTarget(null); }}
        onSaved={() => {
          setCameraTarget(null);
          onImported?.();
          void swrMutate(`zone-providers-for-lc-discovery:${zoneId}`);
        }}
        zoneId={String(zoneId)}
        camera={null}
        provider={null}
        initialName={cameraTarget?.host_name || cameraTarget?.ip || undefined}
        initialIp={cameraTarget?.ip || undefined}
      />

      {nvrTarget && (
        <PeerCreateDialogShell
          title={t('equipment.lc.discovery.actions.addAsNvrTitle')}
          peer={nvrTarget}
          onClose={() => setNvrTarget(null)}
        >
          <NewNVRCard
            zoneId={zoneId}
            initialIp={nvrTarget.ip}
            onCreated={() => {
              setNvrTarget(null);
              onImported?.();
              void swrMutate(`zone-nvrs-for-lc-discovery:${zoneId}`);
            }}
            onCancel={() => setNvrTarget(null)}
          />
        </PeerCreateDialogShell>
      )}

      {routerTarget && (
        <PeerCreateDialogShell
          title={t('equipment.lc.discovery.actions.addAsRouterTitle')}
          peer={routerTarget}
          onClose={() => setRouterTarget(null)}
        >
          <NewRouterCard
            zoneId={zoneId}
            initialGatewayIp={routerTarget.ip}
            onCreated={() => {
              setRouterTarget(null);
              onImported?.();
            }}
            onCancel={() => setRouterTarget(null)}
          />
        </PeerCreateDialogShell>
      )}
    </section>
  );
}

// ---- header --------------------------------------------------------

function ScanHeader({
  status,
  peersCount,
  startedAt,
  isLoading,
  onCancel,
  onRunAgain,
  onDismiss,
}: {
  status: DiscoveryRunStatus;
  peersCount: number;
  startedAt: string | undefined;
  isLoading: boolean;
  onCancel: () => void;
  onRunAgain: () => void;
  onDismiss: () => void;
}) {
  const { t } = useT();
  const elapsed = startedAt ? formatElapsedSeconds(startedAt) : '';
  const live = isLive(status);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary-muted text-primary-muted-foreground">
          <Wifi className="size-4" />
        </span>
        <div className="min-w-0">
          <SectionLabel>{t('equipment.lc.discovery.headerTitle')}</SectionLabel>
          <div className="flex items-baseline gap-2 leading-tight">
            <span
              className={cn(
                'rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]',
                colorForStatus(status),
              )}
            >
              {t(`equipment.discovery.status.${status}`)}
            </span>
            {peersCount > 0 && (
              <span className="text-[11px] tabular-nums text-muted-foreground">
                {t('equipment.lc.discovery.peersCount').replace('{{count}}', String(peersCount))}
              </span>
            )}
            {elapsed && (
              <span className="text-[11px] tabular-nums text-muted-foreground">{elapsed}</span>
            )}
            {isLoading && (
              <Loader2 className="size-3 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {live ? (
          <Button size="sm" variant="outline" onClick={onCancel}>
            <X className="size-3.5" /> {t('equipment.discovery.cancel')}
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={onRunAgain}>
              <RefreshCw className="size-3.5" /> {t('equipment.lc.discovery.runAgain')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              {t('equipment.lc.discovery.dismiss')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function colorForStatus(status: DiscoveryRunStatus): string {
  switch (status) {
    case 'ok':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300';
    case 'partial':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300';
    case 'failed':
    case 'cancelled':
      return 'bg-destructive/15 text-destructive';
    case 'paused_on_creds':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

// ---- create-dialog shell -------------------------------------------
//
// Lightweight modal frame for the "Добавить как NVR" / "Добавить как
// роутер" flows. The inner card components (`NewNVRCard` /
// `NewRouterCard`) already render their own buttons + error banners, so
// the shell only adds the title bar + a peer-identity strip at the top.
function PeerCreateDialogShell({
  title,
  peer,
  onClose,
  children,
}: {
  title: string;
  peer: DiscoveredPeer;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl space-y-3 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="px-4 pb-2 font-mono text-xs">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="tabular-nums">{peer.ip}</span>
            <span className="text-muted-foreground/60">·</span>
            <span className="tabular-nums text-muted-foreground">{peer.mac}</span>
            {peer.host_name && (
              <>
                <span className="text-muted-foreground/60">·</span>
                <span className="font-sans text-foreground/80">{peer.host_name}</span>
              </>
            )}
          </div>
        </div>
        <div className="px-4 pb-4">{children}</div>
      </div>
    </div>
  );
}

// ---- helpers --------------------------------------------------------

function readHash(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash) return null;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return params.get(HASH_KEY);
}

function writeHash(runId: string) {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  params.set(HASH_KEY, runId);
  window.history.replaceState(null, '', `#${params.toString()}`);
}

function clearHash() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  params.delete(HASH_KEY);
  const tail = params.toString();
  window.history.replaceState(
    null,
    '',
    tail ? `#${tail}` : window.location.pathname + window.location.search,
  );
}

function formatElapsedSeconds(startedAt: string): string {
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return '';
  const totalSec = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const mm = String(Math.floor(totalSec / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

/**
 * Translate a streamed `device_found` event log into the normalised
 * `DiscoveredPeer[]` shape Group B's component renders.
 *
 * Dedupe key is the MAC, falling back to IP for events that arrived
 * without a MAC (rare — the scanner usually gets ARP results). The most
 * recently seen event wins on conflict; that's the right behaviour
 * because the worker re-emits when it picks up a vendor refinement on a
 * second pass.
 *
 * Also joins each peer against:
 *   - existing zone providers (by IP)  → `imported_as_camera_id/name`
 *   - existing zone NVRs (by IP)       → `imported_via_nvr_id` marker
 *
 * That's a client-side stand-in for the BE annotations that Group A
 * shipped for router-probe payloads. Once BE adds the same annotations
 * to `device_found` events, this join logic becomes obsolete.
 */
function buildPeersFromEvents(
  events: DiscoveryEvent[],
  providers: Array<{ id: number; camera: number; ip_address: string; name: string }>,
  nvrs: Array<{ id: number; ip_address: string }>,
): DiscoveredPeer[] {
  const byKey = new Map<string, DiscoveredPeer>();
  const providersByIp = new Map<string, { camera: number; name: string }>();
  for (const p of providers) {
    if (p.ip_address) providersByIp.set(p.ip_address, { camera: p.camera, name: p.name });
  }
  const nvrIdsByIp = new Map<string, number>();
  for (const n of nvrs) if (n.ip_address) nvrIdsByIp.set(n.ip_address, n.id);

  for (const evt of events) {
    if (evt.type !== 'device_found') continue;
    const dev = evt.device;
    if (!dev) continue;
    const ip = dev.identity?.ip ?? '';
    const mac = (dev.identity?.mac ?? '').toLowerCase();
    const key = mac || ip;
    if (!key) continue;

    const hostNameRaw = (dev.candidate_fields?.name ?? null) as string | null;
    const kind = inferKind(evt);
    const provHit = providersByIp.get(ip);
    const nvrHit = nvrIdsByIp.get(ip);

    byKey.set(key, {
      mac: mac || ip,
      ip,
      host_name: hostNameRaw,
      active: true,
      kind,
      designation: null,
      imported_as_camera_id: provHit?.camera ?? null,
      imported_as_camera_name: provHit?.name ?? null,
      imported_via_nvr_id: nvrHit ?? null,
      is_jetson: false,
      importable_forward: null,
      other_forwards: [],
    });
  }

  return [...byKey.values()];
}

/**
 * Map BE's `DiscoveredDevice` shape to the `RouterProbeHostKind` axis
 * `DiscoveredPeerList` understands. The classification draws from:
 *   - `device.hints.device_role` ("ipc" → camera, "nvr" → nvr) — most
 *     trustworthy when present (post-realm-probe).
 *   - `device.vendor` — coarse vendor hint (e.g. "routeros" → router).
 *   - vendor_guess fallback.
 */
function inferKind(evt: DiscoveryEvent): RouterProbeHostKind {
  const dev = evt.device;
  if (!dev) return 'unknown';
  const role = dev.hints?.device_role;
  if (role === 'ipc') return 'camera';
  if (role === 'nvr') return 'nvr';
  const vendor = dev.vendor ?? dev.hints?.vendor_guess;
  if (vendor === 'routeros') return 'likely_other'; // surfaced as "router-ish" via designate
  if (vendor === 'hikvision') return 'camera';      // common-case bias; operator can override
  if (vendor === 'generic-rtsp') return 'camera';
  return 'unknown';
}

export default LocalComputerDiscoveryPanel;
